"use client";

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { ApiResponse } from "@/shared/types/interface/api";
import { getHealthInfoDetailClient, getHealthInfoListClient, getHospitalHealthInfoListClient } from "../api/health-info.client";
import { HOSPITAL_HEALTH_INFO_PAGE_SIZE } from "../api/url";
import type { HealthInfoListResponse } from "../model/types";
import { PRESERVE_CACHED_DATA_META } from "@/shared/lib/query-error-policy";
export const useQueryHealthInfoList = (t0, t1) => {
  const $ = _c(3);
  const page = t0 === undefined ? 1 : t0;
  const size = t1 === undefined ? 1000 : t1;
  let t2;
  if ($[0] !== page || $[1] !== size) {
    t2 = {
      queryKey: ["healthInfoList", page, size],
      queryFn: () => getHealthInfoListClient(page, size)
    };
    $[0] = page;
    $[1] = size;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
};
export const useQueryHealthInfoDetail = id => {
  const $ = _c(8);
  let t0;
  let t1;
  let t2;
  if ($[0] !== id) {
    t0 = ["healthInfoDetail", id];
    t1 = () => getHealthInfoDetailClient(id);
    t2 = Number.isSafeInteger(id) && id > 0;
    $[0] = id;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useInfiniteQueryHospitalHealthInfoList = (hospitalOriginId, initialResponse, t0) => {
  const $ = _c(20);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== hospitalOriginId) {
    t1 = ["hospitalHealthInfoList", hospitalOriginId, HOSPITAL_HEALTH_INFO_PAGE_SIZE];
    $[0] = hospitalOriginId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hospitalOriginId) {
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return getHospitalHealthInfoListClient(hospitalOriginId, pageParam as number, HOSPITAL_HEALTH_INFO_PAGE_SIZE);
    };
    $[2] = hospitalOriginId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== initialResponse) {
    t3 = [initialResponse];
    $[4] = initialResponse;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [1];
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = {
      pages: t3,
      pageParams: t4
    };
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== enabled || $[10] !== hospitalOriginId) {
    t6 = enabled && Number.isSafeInteger(hospitalOriginId) && hospitalOriginId > 0;
    $[9] = enabled;
    $[10] = hospitalOriginId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== hospitalOriginId) {
    t7 = (lastPage, pages) => {
      const priorIds = new Set(pages.slice(0, -1).flatMap(page => page.result.contents.filter(item => item.hospitalOriginId === hospitalOriginId).map(_temp)));
      const allIds = new Set(priorIds);
      for (const item_1 of lastPage.result.contents) {
        if (item_1.hospitalOriginId === hospitalOriginId) {
          allIds.add(item_1.id);
        }
      }
      if (allIds.size === priorIds.size || allIds.size >= lastPage.result.total) {
        return;
      }
      return pages.length + 1;
    };
    $[12] = hospitalOriginId;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = {
      queryKey: t1,
      meta: PRESERVE_CACHED_DATA_META,
      queryFn: t2,
      initialPageParam: 1,
      initialData: t5,
      staleTime: 3600000,
      enabled: t6,
      getNextPageParam: t7
    };
    $[14] = t1;
    $[15] = t2;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return useInfiniteQuery(t8);
};
function _temp(item_0) {
  return item_0.id;
}